
import { Minus, Plus } from 'lucide-react'
import * as React from 'react'

import { Button } from './button'

import { cn } from '@/lib/utils'


export interface NumberInputProps
  extends Omit<React.ComponentProps<'input'>, 'onChange' | 'value'> {
  value?: number | null
  onChange?: (value: number | null) => void
  onValueChange?: (e: { value: number | null }) => void
  showButtons?: boolean
  min?: number
  max?: number
  step?: number
  buttonLayout?: 'horizontal' | 'vertical'
  inputClassName?: string
  incrementButtonClassName?: string
  decrementButtonClassName?: string
  incrementButtonIcon?: string
  decrementButtonIcon?: string
  useGrouping?: boolean
  locale?: string
  label?: string
  labelClassName?: string
  inputId?: string
  inputRef?: React.Ref<HTMLInputElement>
  suffix?: string
}

const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      className,
      inputClassName,
      incrementButtonClassName,
      decrementButtonClassName,
      value,
      onChange,
      onValueChange,
      showButtons = false,
      min,
      max,
      step = 1,
      buttonLayout = 'vertical',
      label,
      labelClassName,
      inputId,
      inputRef,
      suffix,
      locale: _locale,
      ...props
    },
    ref
  ) => {
    const handleIncrement = () => {
      const currentValue = value ?? 0
      const newValue = currentValue + step
      if (max !== undefined && newValue > max) return

      const updatedValue = newValue
      onChange?.(updatedValue)
      onValueChange?.({ value: updatedValue })
    }

    const handleDecrement = () => {
      const currentValue = value ?? 0
      const newValue = currentValue - step
      if (min !== undefined && newValue < min) return

      const updatedValue = newValue
      onChange?.(updatedValue)
      onValueChange?.({ value: updatedValue })
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value
      if (val === '') {
        onChange?.(null)
        onValueChange?.({ value: null })
        return
      }

      const numValue = parseFloat(val)
      if (isNaN(numValue)) return

      if (min !== undefined && numValue < min) return
      if (max !== undefined && numValue > max) return

      onChange?.(numValue)
      onValueChange?.({ value: numValue })
    }

    if (!showButtons) {
      const input = suffix ? (
        <div className="relative inline-block w-full">
          {/* Background layer with value + suffix */}
          <div className="pointer-events-none absolute inset-0 flex h-9 items-center rounded-md border border-transparent bg-white px-3 py-1 text-base md:text-sm">
            <span className="invisible">{value ?? ''}</span>
            <span className="text-muted-foreground/60 ml-1">{suffix}</span>
          </div>
          {/* Actual input with transparent background */}
          <input
            type="number"
            id={inputId}
            ref={inputRef || ref}
            value={value ?? ''}
            onChange={handleInputChange}
            min={min}
            max={max}
            step={step}
            className={cn(
              'border-input file:text-foreground placeholder:text-muted-foreground relative flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:shadow-[0_0_0_0.2rem_hsl(var(--primary)_/_0.3)] focus-visible:outline-none focus-visible:outline-offset-0 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
              inputClassName,
              className
            )}
            {...props}
          />
        </div>
      ) : (
        <input
          type="number"
          id={inputId}
          ref={inputRef || ref}
          value={value ?? ''}
          onChange={handleInputChange}
          min={min}
          max={max}
          step={step}
          className={cn(
            'border-input file:text-foreground placeholder:text-muted-foreground flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:shadow-[0_0_0_0.2rem_hsl(var(--primary)_/_0.3)] focus-visible:outline-none focus-visible:outline-offset-0 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
            inputClassName,
            className
          )}
          {...props}
        />
      )

      if (label) {
        return (
          <div className="space-y-2">
            <label htmlFor={inputId} className={cn('text-sm font-medium', labelClassName)}>
              {label}
            </label>
            {input}
          </div>
        )
      }

      return input
    }

    if (buttonLayout === 'horizontal') {
      const horizontalInput = (
        <div className={cn('inline-flex', className)}>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className={cn('h-9 rounded-r-none border-r-0', decrementButtonClassName)}
            onClick={handleDecrement}
            disabled={min !== undefined && (value ?? 0) <= min}
          >
            <Minus className="h-4 w-4" />
          </Button>
          <div
            className={cn('relative', {
              'flex-1': suffix,
            })}
          >
            {suffix && (
              <div className="pointer-events-none absolute inset-0 flex h-9 items-center justify-center rounded-none bg-white px-3 py-1 text-base md:text-sm">
                <span className="invisible">{value ?? ''}</span>
                <span className="text-muted-foreground ml-1">{suffix}</span>
              </div>
            )}
            <input
              type="number"
              ref={ref}
              value={value ?? ''}
              onChange={handleInputChange}
              min={min}
              max={max}
              step={step}
              className={cn(
                'border-input placeholder:text-muted-foreground relative flex h-9 w-full rounded-none border bg-transparent px-3 py-1 text-center text-base shadow-sm transition-colors [appearance:textfield] focus-visible:shadow-[0_0_0_0.2rem_hsl(var(--primary)_/_0.3)] focus-visible:outline-none focus-visible:outline-offset-0 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
                inputClassName
              )}
              {...props}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className={cn('h-9 rounded-l-none border-l-0', incrementButtonClassName)}
            onClick={handleIncrement}
            disabled={max !== undefined && (value ?? 0) >= max}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      )

      return horizontalInput
    }

    // Vertical layout
    const verticalInput = (
      <div className={cn('inline-flex flex-col', className)}>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={cn('h-[18px] w-full rounded-b-none border-b-0 px-0', incrementButtonClassName)}
          onClick={handleIncrement}
          disabled={max !== undefined && (value ?? 0) >= max}
        >
          <Plus className="h-3 w-3" />
        </Button>
        <div className="relative">
          {suffix && (
            <div className="pointer-events-none absolute inset-0 flex h-9 items-center justify-center rounded-none bg-white px-3 py-1 text-base md:text-sm">
              <span className="invisible">{value ?? ''}</span>
              <span className="text-muted-foreground ml-1">{suffix}</span>
            </div>
          )}
          <input
            type="number"
            ref={ref}
            value={value ?? ''}
            onChange={handleInputChange}
            min={min}
            max={max}
            step={step}
            className={cn(
              'border-input placeholder:text-muted-foreground relative flex h-9 w-full rounded-none border bg-transparent px-3 py-1 text-center text-base shadow-sm transition-colors [appearance:textfield] focus-visible:shadow-[0_0_0_0.2rem_hsl(var(--primary)_/_0.3)] focus-visible:outline-none focus-visible:outline-offset-0 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
              inputClassName
            )}
            {...props}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={cn('h-[18px] w-full rounded-t-none border-t-0 px-0', decrementButtonClassName)}
          onClick={handleDecrement}
          disabled={min !== undefined && (value ?? 0) <= min}
        >
          <Minus className="h-3 w-3" />
        </Button>
      </div>
    )

    return verticalInput
  }
)

NumberInput.displayName = 'NumberInput'

export { NumberInput }
